package ui

import io.kvision.core.BoxShadow
import io.kvision.core.Border
import io.kvision.core.BorderStyle
import io.kvision.core.Col
import io.kvision.core.Color
import io.kvision.core.Background
import io.kvision.core.Display
import io.kvision.utils.perc
import io.kvision.core.FlexDirection
import io.kvision.core.FontWeight
import io.kvision.core.JustifyContent
import io.kvision.core.Overflow
import io.kvision.core.Position
import io.kvision.core.TextAlign
import io.kvision.core.AlignItems
import io.kvision.core.onClick
import io.kvision.html.button
import io.kvision.html.h2
import io.kvision.html.h3
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.px
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import globals.KEnv
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.ttt.autogenesis.kvisionapp.ServerExtendBridge
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.RpcJson
import structs.rpcRequests.GetPlayerMapRequest
import structs.rpcRequests.GetPlayerMapResponse

/**
 * Detail window for a single map. Mirrors CommanderDetailWindow.kt:539.
 * Reuses the login-widget-window class so it inherits the auto-height
 * recipe + mobile-portrait overrides. The Download button uses the
 * existing Blob/URL.createObjectURL pattern at StoryDetailWindow.kt:733.
 *
 * Reads from MapDisplayInfo to populate the fields. The .map blob itself
 * is wired via the TODO_AFTER_UPLOAD_RPC marker in downloadCurrentMap().
 */
class MapDetailWindow : SimplePanel(className = "login-widget-window")
{
    private var currentMap: MapDisplayInfo? = null

    init
    {
        width = 700.px
        height = 600.px
        position = Position.FIXED
        top = 50.perc
        left = 50.perc
        marginTop = (-300).px
        marginLeft = (-350).px
        zIndex = 9100
        padding = 25.px
        border = Border(2.px, BorderStyle.SOLID, Color("rgba(94, 106, 220, 0.5)"))
        background = Background(Color("rgba(8, 10, 24, 0.95)"))
        boxShadow = BoxShadow(0.px, 25.px, 50.px, color = Color("rgba(0, 0, 0, 0.85)"))
        flexDirection = FlexDirection.COLUMN
        display = Display.NONE

        // Stable e2e selectors — mirror the MainMenu.kt:55-64 + CollectionOverlay.kt:55+
        // pattern. The `data-testid` makes the root discoverable by Playwright probes;
        // the `data-mobile-layout` attribute is set by the matchMedia listener below
        // and tells the probe (and any future code) which layout shape the widget is
        // currently in.
        setAttribute("data-testid", "map-detail-window")

        // Mobile layout listener — same shape as MainMenu.kt:250-280, CollectionOverlay.kt:385-414,
        // CommanderSelectionDialog.kt, CommanderCreationDialog.kt, MessageBox.kt, etc. The
        // break-even point and matchMedia query are pinned at (max-width: 600px) /
        // (max-height: 600px) + portrait so every widget across the codebase reacts to the
        // same viewport threshold and the e2e probe can flip state by resizing the page.
        // Note: the load-bearing CSS rules live in night-mode.css under a labeled
        // `@media (max-width: 600px), (max-height: 600px) and (orientation: portrait)` block
        // for `.map-detail-*` selectors — this listener only owns the attribute, not the
        // CSS, mirroring how MainMenu + the other widgets factor it.
        kotlinx.coroutines.GlobalScope.launch {
            try {
                val mediaQuery = kotlinx.browser.window.matchMedia(
                    "(max-width: 600px), (max-height: 600px) and (orientation: portrait)"
                )
                val updateAttribute: (Boolean) -> Unit = { matches ->
                    setAttribute("data-mobile-layout", if (matches) "portrait" else "desktop")
                }
                updateAttribute(mediaQuery.matches)
                mediaQuery.addEventListener("change", { event ->
                    val mql = event.asDynamic()
                    updateAttribute(mql.matches as Boolean)
                })
                Logger.info(
                    LogCategory.UI,
                    "MapDetailWindow: matchMedia listener attached, current state=${if (mediaQuery.matches) "portrait" else "desktop"}"
                )
            } catch (err: Throwable) {
                Logger.warn(
                    LogCategory.UI,
                    "MapDetailWindow: matchMedia listener failed to attach (non-fatal): ${err.message}"
                )
            }
        }

        h2("Map Details") {
            color = Color.name(Col.CYAN)
            marginTop = 0.px
            marginBottom = 12.px
            textAlign = TextAlign.CENTER
            setAttribute("data-testid", "map-detail-title")
        }

        vPanel {
            width = 100.perc
            flexGrow = 1
            overflow = Overflow.AUTO
            spacing = 12
            h3("") {
                color = Color.name(Col.WHITE)
                marginTop = 0.px
                marginBottom = 4.px
                setAttribute("data-testid", "map-detail-name")
            }
            span("Metadata") {
                color = Color.name(Col.LIGHTGRAY)
                fontSize = 14.px
                display = Display.BLOCK
                marginBottom = 8.px
                setAttribute("data-testid", "map-detail-meta")
            }
            p("Author") {
                color = Color.name(Col.LIGHTBLUE)
                fontSize = 14.px
                fontWeight = FontWeight.BOLD
                marginBottom = 4.px
            }
            span("Author") {
                color = Color.name(Col.WHITE)
                setAttribute("data-testid", "map-detail-author")
            }
            p("Player Count") {
                color = Color.name(Col.LIGHTBLUE)
                fontSize = 14.px
                fontWeight = FontWeight.BOLD
                marginTop = 8.px
                marginBottom = 4.px
            }
            span("Player Count") {
                color = Color.name(Col.WHITE)
                setAttribute("data-testid", "map-detail-players")
            }
            p("Territory Count") {
                color = Color.name(Col.LIGHTBLUE)
                fontSize = 14.px
                fontWeight = FontWeight.BOLD
                marginTop = 8.px
                marginBottom = 4.px
            }
            span("Territory Count") {
                color = Color.name(Col.WHITE)
                setAttribute("data-testid", "map-detail-territories")
            }
            p("Schema Version") {
                color = Color.name(Col.LIGHTBLUE)
                fontSize = 14.px
                fontWeight = FontWeight.BOLD
                marginTop = 8.px
                marginBottom = 4.px
            }
            span("Schema Version") {
                color = Color.name(Col.WHITE)
                setAttribute("data-testid", "map-detail-schema")
            }
            p("Map Pack SHA") {
                color = Color.name(Col.LIGHTBLUE)
                fontSize = 14.px
                fontWeight = FontWeight.BOLD
                marginTop = 8.px
                marginBottom = 4.px
            }
            span("Map Pack SHA") {
                color = Color.name(Col.WHITE)
                fontFamily = "ui-monospace, SF Mono, monospace"
                fontSize = 12.px
                setAttribute("data-testid", "map-detail-sha")
            }
        }

        hPanel(justify = JustifyContent.CENTER, alignItems = AlignItems.CENTER, spacing = 12) {
            width = 100.perc
            button("DELETE", icon = "fas fa-trash", className = "btn btn-secondary-action map-detail-delete") {
                onClick { confirmDeleteCurrentMap() }
                setAttribute("data-testid", "map-detail-delete")
            }
            button("Download .map", icon = "fas fa-download", className = "btn btn-secondary-action") {
                onClick { kotlinx.coroutines.MainScope().launch { downloadCurrentMap() } }
                setAttribute("data-testid", "map-detail-download")
            }
            button("CLOSE", icon = "fas fa-times", className = "btn btn-secondary-action") {
                onClick { this@MapDetailWindow.hide() }
                setAttribute("data-testid", "map-detail-close")
            }
        }
    }

    internal fun show(info: MapDisplayInfo)
    {
        currentMap = info
        Logger.info(LogCategory.UI, "MapDetailWindow: show() map_id=${info.id} map_name=\"${info.name}\"")
        // Populate the named spans via querySelector — pragmatic for the v1
        // modal; refactor to direct refs in a follow-up if needed.
        val nameEl = document.querySelector("[data-testid='map-detail-name']")
        if (nameEl != null) { nameEl.textContent = info.name; Logger.info(LogCategory.UI, "MapDetailWindow: field=name value=\"${info.name}\"") }
        val metaEl = document.querySelector("[data-testid='map-detail-meta']")
        if (metaEl != null) metaEl.textContent = info.subtitle
        val authorEl = document.querySelector("[data-testid='map-detail-author']")
        if (authorEl != null) { authorEl.textContent = info.author; Logger.info(LogCategory.UI, "MapDetailWindow: field=author value=\"${info.author}\"") }
        val playersEl = document.querySelector("[data-testid='map-detail-players']")
        if (playersEl != null) { playersEl.textContent = info.playerCount; Logger.info(LogCategory.UI, "MapDetailWindow: field=players value=\"${info.playerCount}\"") }
        val territoriesEl = document.querySelector("[data-testid='map-detail-territories']")
        if (territoriesEl != null) { territoriesEl.textContent = info.territoryCount; Logger.info(LogCategory.UI, "MapDetailWindow: field=territories value=\"${info.territoryCount}\"") }
        val schemaEl = document.querySelector("[data-testid='map-detail-schema']")
        if (schemaEl != null) { schemaEl.textContent = info.schemaVersion; Logger.info(LogCategory.UI, "MapDetailWindow: field=schema value=\"${info.schemaVersion}\"") }
        val shaEl = document.querySelector("[data-testid='map-detail-sha']")
        if (shaEl != null) { shaEl.textContent = info.packSha; Logger.info(LogCategory.UI, "MapDetailWindow: field=sha value=\"${info.packSha}\"") }
        display = Display.FLEX
        visible = true
    }

    private suspend fun downloadCurrentMap()
    {
        val map = currentMap ?: return
        Logger.info(LogCategory.UI, "MapDetailWindow: download click map_id=${map.id} map_name=\"${map.name}\"")
        val invoker = ServerExtendBridge.rpcInvoker
        if (invoker == null)
        {
            Logger.warn(LogCategory.UI, "MapDetailWindow: download aborted — ServerExtendBridge.rpcInvoker is null")
            return
        }
        // Show a transient "preparing download…" MessageBox for the long
        // round-trip + AGS propagation window.
        val preparing = MessageBox(
            boxTitle = "Preparing download…",
            message = "Fetching map pack from AGS. This may take a few seconds.",
            showThrobber = true
        )
        KEnv.mainRoot?.add(preparing)
        try
        {
            // The userId is sourced from the AccelByteEnv singleton.
            // We don't propagate it through MapDisplayInfo yet because
            // the catalogue currently surfaces only the user's own
            // uploads (decision #9: user uploads only). When the
            // collection later surfaces other users' maps,
            // MapDisplayInfo will need an ownerUserId field.
            val userId = globals.AccelByteEnv.userId
            val response = invoker.invoke(
                "server.extend.getPlayerMap",
                GetPlayerMapRequest(userId = userId, mapId = map.id),
                GetPlayerMapRequest.serializer()
            )
            preparing.hide()
            if (response.error != null)
            {
                val errMsg = response.error?.message ?: response.error?.code?.toString() ?: "Unknown RPC error"
                Logger.warn(LogCategory.UI, "MapDetailWindow: getPlayerMap RPC error: $errMsg")
                showDownloadFailure(errMsg)
                return
            }
            val payload = response.result
                ?: run {
                    Logger.warn(LogCategory.UI, "MapDetailWindow: getPlayerMap returned null result payload")
                    showDownloadFailure("empty response from server")
                    return
                }
            val response2 = RpcJson.decodeFromJsonElement(GetPlayerMapResponse.serializer(), payload)
            // Bytes transport: server-side `getPlayerMap` now returns
            // only the presigned URL (the `mapPackBytes` field is empty)
            // to dodge the 6.3 MB → 8.4 MB base64 JSON-encode that froze
            // the SSE writer. We have two paths:
            //   1. `response2.downloadUrl` is set → fetch the bytes from
            //      the binary-stream endpoint and trigger a download.
            //   2. `response2.downloadUrl` is null AND `mapPackBytes` is
            //      non-empty → legacy path, use the inline bytes. This
            //      branch only fires for older server-extend builds that
            //      haven't picked up the presigned-URL change yet.
            // The user-reported regression ("zero-byte file gets
            // downloaded") came from this code reading
            // `response2.mapPackBytes` unconditionally — for the new
            // server that's the empty default, hence the 0-byte file.
            val downloadUrl = response2.downloadUrl
            val bytes: ByteArray =
                if (downloadUrl != null && downloadUrl.isNotBlank())
                {
                    Logger.info(LogCategory.UI, "MapDetailWindow: streaming 6.3 MB pack via presigned URL $downloadUrl")
                    fetchBytesFromUrl(downloadUrl)
                }
                else if (response2.mapPackBytes.isNotEmpty())
                {
                    Logger.warn(
                        LogCategory.UI,
                        "MapDetailWindow: server returned inline mapPackBytes (size=${response2.mapPackBytes.size}); the legacy path. Update server-extend."
                    )
                    response2.mapPackBytes
                }
                else
                {
                    showDownloadFailure("server returned neither downloadUrl nor bytes")
                    return
                }
            triggerBrowserDownload(bytes, map.name)
        }
        catch (e: Throwable)
        {
            preparing.hide()
            Logger.error(LogCategory.UI, "MapDetailWindow: download threw: ${e.message}")
            showDownloadFailure(e.message ?: "unknown")
        }
    }

    private fun showDownloadFailure(detail: String)
    {
        val err = MessageBox(
            boxTitle = "Download failed",
            message = "Download failed: $detail"
        )
        KEnv.mainRoot?.add(err)
    }

    /**
     * Streams the raw map-pack bytes from a server-extend URL via the
     * standard `fetch().then(r => r.arrayBuffer())` pipeline. Returns
     * a Kotlin `ByteArray` that [triggerBrowserDownload] can hand to
     * `Blob(arrayOf(bytes))` for the file-save dialog. The
     * `arrayBuffer` path (rather than `blob()`) avoids a copy inside
     * the browser — the `ByteArray` wrapper is an O(n) typed-array
     * view, and `Blob` reads directly from the underlying buffer.
     *
     * Why this lives in MapDetailWindow and not CollectionOverlay: the
     * download only runs from this dialog, never from the catalogue's
     * thumbnail render, so duplicating the trivial `js()` fetch
     * wrapper here is cheaper than threading a common helper through
     * both call sites. If a third caller appears, hoist into a
     * shared util.
     */
    private suspend fun fetchBytesFromUrl(url: String): ByteArray
    {
        val promise: kotlin.js.Promise<org.khronos.webgl.Uint8Array> = kotlin.js.Promise { resolve, reject ->
            try
            {
                js(
                    "fetch(url).then(function(r){return r.arrayBuffer();}).then(function(ab){resolve(new Uint8Array(ab));}).catch(function(e){reject(e);});"
                )
            }
            catch (e: Throwable)
            {
                reject(e)
            }
        }
        val u8 = promise.await()
        val bytes = ByteArray(u8.length)
        for (i in 0 until u8.length)
        {
            bytes[i] = (u8.asDynamic()[i] as Int).toByte()
        }
        return bytes
    }

    private fun confirmDeleteCurrentMap()
    {
        val map = currentMap ?: return
        Logger.info(LogCategory.UI, "MapDetailWindow: delete click map_id=${map.id} map_name=\"${map.name}\"")
        val confirm = MessageBox(
            boxTitle = "Delete map?",
            message = "Permanently delete '${map.name}'? This cannot be undone.",
            showOk = true,
            showCancel = true
        )
        confirm.onConfirm = {
            confirm.hide()
            dispatchDeleteMap(map)
        }
        KEnv.mainRoot?.add(confirm)
    }

    private fun dispatchDeleteMap(map: MapDisplayInfo)
    {
        val invoker = ServerExtendBridge.rpcInvoker
        if (invoker == null)
        {
            val err = MessageBox(
                boxTitle = "Delete failed",
                message = "Delete failed: not connected to server",
                showOk = true
            )
            err.onConfirm = { err.hide() }
            KEnv.mainRoot?.add(err)
            return
        }
        val throbber = MessageBox(
            boxTitle = "Deleting map…",
            message = "Removing '${map.name}' from your collection. This may take a few seconds.",
            showThrobber = true
        )
        KEnv.mainRoot?.add(throbber)
        MainScope().launch {
            try
            {
                val userId = globals.AccelByteEnv.userId
                val response = invoker.invoke(
                    "server.extend.deletePlayerMap",
                    structs.rpcRequests.DeletePlayerMapRequest(userId = userId, mapId = map.id),
                    structs.rpcRequests.DeletePlayerMapRequest.serializer()
                )
                throbber.hide()
                if (response.error != null)
                {
                    val errMsg = response.error?.message ?: response.error?.code?.toString() ?: "Unknown RPC error"
                    val err = MessageBox(
                        boxTitle = "Delete failed",
                        message = "Delete failed: $errMsg",
                        showOk = true
                    )
                    err.onConfirm = { err.hide() }
                    KEnv.mainRoot?.add(err)
                    return@launch
                }
                val ack = RpcJson.decodeFromJsonElement(
                    structs.rpcRequests.DeletePlayerMapResult.serializer(),
                    response.result ?: return@launch
                )
                Logger.info(LogCategory.UI, "MapDetailWindow: deletePlayerMap ack deleted=${ack.deleted} mapId=${map.id}")
            }
            catch (e: Throwable)
            {
                throbber.hide()
                val err = MessageBox(
                    boxTitle = "Delete failed",
                    message = "Delete failed: ${e.message ?: "unknown"}",
                    showOk = true
                )
                err.onConfirm = { err.hide() }
                KEnv.mainRoot?.add(err)
            }
        }
    }

    private fun triggerBrowserDownload(bytes: ByteArray, mapName: String)
    {
        val safeName = mapName.replace("\\s+".toRegex(), "-").lowercase()
        val blob = Blob(arrayOf(bytes), BlobPropertyBag(type = "application/octet-stream"))
        val url = URL.createObjectURL(blob)
        val anchor = (document.createElement("a") as HTMLAnchorElement).apply {
            href = url
            download = "$safeName.map"
        }
        document.body?.appendChild(anchor)
        anchor.click()
        document.body?.removeChild(anchor)
        URL.revokeObjectURL(url)
    }
    override fun hide()
    {
        currentMap?.let { Logger.info(LogCategory.UI, "MapDetailWindow: hide() map_id=${it.id} map_name=\"${it.name}\"") }
        super.hide()
        display = Display.NONE
        visible = false
    }
}
